import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import { useEffect, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { changeContractDueDate } from 'services/contract';
import { Mixpanel } from 'services/mixpanel';
import { Contract } from 'shared/models/contract';
import { useFinancialContext } from '../../FinancialContext';

type DueDay = 5 | 10 | 15 | 20;

interface ChangeDueDateModalProps {
  isOpen: boolean;
  onClose(): void;
  contract: Contract;
}

const ChangeDueDateModal: React.FC<ChangeDueDateModalProps> = ({
  isOpen,
  onClose: onCloseFromProps,
  contract,
}) => {
  const { confirm } = useGlobalContext();
  const { reloadContracts } = useFinancialContext();

  const [loading, setLoading] = useState(false);
  const [dueDate, setDueDate] = useState<DueDay>(
    (contract.due_date?.date() as DueDay) ?? 5,
  );

  const onClose = () => {
    onCloseFromProps();
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && contract?.due_date) {
      setDueDate(contract.due_date.date() as DueDay);
    }
  }, [isOpen, contract]);

  const submit = () => {
    if (!dueDate || !contract) {
      return toast.error(
        'Página cacheada, por favor recarregue com F5 ou Ctrl + R',
        { autoClose: 5000 },
      );
    }

    confirm(
      `Alterar vencimento para dia ${dueDate}`,
      'Deseja continuar?',
      async () => {
        Mixpanel.track('customers-financial-due-date-submitted-new-due-date');
        setLoading(true);
        try {
          await changeContractDueDate(contract.id!, dueDate);
          await reloadContracts(false, contract);

          toast.success('Data de vencimento alterada');
          onCloseFromProps();
        } catch (e) {
          const axiosError = e as AxiosError;
          toast.error('Falha ao alterar data de vencimento');
          console.error(
            'Falha ao alterar data de vencimento',
            axiosError?.response?.data
              ? JSON.stringify(axiosError?.response?.data, null, 2)
              : JSON.stringify(axiosError?.response, null, 2),
          );
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent key={contract.id}>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>Alterar Data de Vencimento</ModalHeader>

        <ModalBody>
          <Card bg="blue.100">
            <CardBody>
              <HStack gap="1rem">
                <Icon as={MdInfoOutline} color="blue.500" />
                <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                  Ao realizar a alteração do vencimento do contrato, a próxima
                  cobrança terá o valor reajustado
                </Text>
              </HStack>
            </CardBody>
          </Card>

          <FormControl mt="1rem">
            <FormLabel fontSize="0.8rem" color="gray.500">
              Dia de vencimento
            </FormLabel>
            <Select
              id="select-due-date"
              value={String(dueDate)}
              onChange={(e) => setDueDate(Number(e.target.value) as DueDay)}
              isDisabled={loading}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            onClick={submit}
            isDisabled={loading || dueDate === contract?.due_date?.date()}
            colorScheme="yooga"
            w="100%"
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeDueDateModal;
